import React, { useEffect, useRef, useState, useContext } from "react";
import { Container, Snackbar, Alert, Typography } from "@mui/material";
import ScannedTicketDetails from "./ScannedTicketDetails";
import PendingTicketCard from "./PendingTicketCard";
import { Html5QrcodeScanner } from "html5-qrcode";
import { openDB } from "idb";
import CryptoJS from "crypto-js";
import { AuthContext } from "./AuthContext";

const SECRET_KEY =
  "eWVraW5hYnVkYmVuYW1laG9uYXJhZmFyaW5laG9uYXJtYW5keWVraWJ1ZA=="; // Secret key for checksum validation

const SYNC_INTERVAL = 30000; // Background sync every 10 seconds
const COOLDOWN_DELAY = 2000; // 2s cooldown after duplicate scan

const TicketScanner = () => {
  const scannerRef = useRef(null);
  const syncRunning = useRef(false);
  const lastScannedTicket = useRef(null);
  const cooldownTimer = useRef(null);
  // Accepted tickets from local DB
  const scannedTicketsRef = useRef(new Set());
  // Pending tickets state
  const [pendingTickets, setPendingTickets] = useState([]);
  // Use a ref to always have the latest pendingTickets array
  const pendingTicketsRef = useRef([]);
  useEffect(() => {
    pendingTicketsRef.current = pendingTickets;
  }, [pendingTickets]);

  const { user, accessToken } = useContext(AuthContext);
  const [lastScannedData, setLastScannedData] = useState(null);
  let lastErrorTime = 0; // Prevents error spam

  // Toast state for invalid QR codes
  const [toast, setToast] = useState({ open: false, message: "", severity: "error" });

  const playSound = (type) => {
    const audio = new Audio(type === "valid" ? "/sounds/beep-valid.mp3" : "/sounds/beep-error.mp3");
    audio.play();
  };

  useEffect(() => {
    initScanner();
    initLocalDB();
    startBackgroundSync();
  }, []);

  const initScanner = () => {
    if (scannerRef.current) return;

    // Pass videoConstraints to force using the environment (rear) camera
    scannerRef.current = new Html5QrcodeScanner("reader", {
      fps: 10,
      qrbox: { width: 250, height: 250 },
      videoConstraints: { facingMode: "environment" },
    });

    scannerRef.current.render(handleScan, handleError);

    return () => {
      scannerRef.current.clear();
      scannerRef.current = null;
    };
  };

  const initLocalDB = async () => {
    try {
      const db = await openDB("ticketDB", 1, {
        upgrade(db) {
          if (!db.objectStoreNames.contains("scanned")) {
            db.createObjectStore("scanned", { keyPath: "id" });
          }
        },
      });
      const tickets = await db.getAll("scanned");
      scannedTicketsRef.current = new Set(tickets.map((t) => t.id));
      console.log(`✅ Loaded ${tickets.length} accepted tickets from local DB`);
    } catch (err) {
      console.error("❌ IndexedDB error:", err);
    }
  };

  const handleScan = async (qrContent) => {
    const validatedData = verifyQR(qrContent);
    if (!validatedData) {
      console.warn("❌ کد QR نامعتبر");
      playSound("error");
      setToast({ open: true, message: "کد QR نامعتبر است", severity: "error" });
      return;
    }

    const { ticketId, seatInfo } = validatedData;

    // Cooldown to prevent rapid duplicate triggers
    if (lastScannedTicket.current === ticketId) {
      if (cooldownTimer.current) clearTimeout(cooldownTimer.current);
      cooldownTimer.current = setTimeout(() => {
        lastScannedTicket.current = null;
      }, COOLDOWN_DELAY);
      return;
    }
    lastScannedTicket.current = ticketId;

    // Prevent adding a ticket twice in pending array or if it's accepted
    if(scannedTicketsRef.current.has(ticketId)){
         // Update existing pending ticket's duplicate flag
        setPendingTickets((prev) =>
            prev.map((t) => (t.ticketId === ticketId ? { ...t, duplicate: true } : t))
        );
        playSound("error");
        console.warn(`🚫 تکراری: ${ticketId}`);
        setToast({ open: true, message: "بلیط تکراری", severity: "error" })
        return;
    }
    if (
      pendingTicketsRef.current.some((t) => t.ticketId === ticketId)
    ) {
      // Update existing pending ticket's duplicate flag
      
      playSound("error");
      console.warn(`🚫 تکراری: ${ticketId}`);
      setToast({ open: true, message: "بلیط تکراری", severity: "error" })
      return;
    }

    // Add as a new pending ticket
    const newTicket = { ticketId, seatInfo, duplicate: false };
    setPendingTickets((prev) => [...prev, newTicket]);
    playSound("valid");
    console.log(`✅ بلیط اسکن شده: ${ticketId}`, seatInfo);
  };

  const handleError = (error) => {
    const now = Date.now();
    if (now - lastErrorTime > 5000) {
      console.error("Scanning error:", error.message);
      lastErrorTime = now;
    }
  };

  // Accept ticket (swipe right)
  const acceptTicket = async (ticket) => {
    if (ticket.duplicate) {
      console.warn("❌ بلیط تکراری نمی‌تواند تایید شود");
      return;
    }
    await saveToLocalDB(ticket.ticketId, user.username);
    scannedTicketsRef.current.add(ticket.ticketId);
    setPendingTickets((prev) => prev.filter((t) => t.ticketId !== ticket.ticketId));
    setLastScannedData(ticket.seatInfo);
    console.log(`✅ بلیط تایید شده: ${ticket.ticketId}`);
  };

  // Reject ticket (swipe left)
  const rejectTicket = (ticket) => {
    setPendingTickets((prev) => prev.filter((t) => t.ticketId !== ticket.ticketId));
    console.log(`❌ بلیط رد شده: ${ticket.ticketId}`);
  };

  const saveToLocalDB = async (ticketId, username) => {
    try {
      const db = await openDB("ticketDB", 1);
      const tx = db.transaction("scanned", "readwrite");
      await tx.objectStore("scanned").put({ id: ticketId, username, synced: false });
      console.log(`✅ ذخیره بلیط: ${ticketId} توسط ${username}`);
    } catch (err) {
      console.error("❌ IndexedDB save error:", err);
    }
  };

  const startBackgroundSync = () => {
    setInterval(async () => {
      if (syncRunning.current) return;
      syncRunning.current = true;
      try {
        const db = await openDB("ticketDB", 1);
        const localTickets = await db.getAll("scanned");

        // Step 1: Send local tickets to server
        let response = await fetch("https://api.onacademyon.com/staff/sync-seats", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            usertoken: accessToken,
          },
          body: JSON.stringify({ newSeats: localTickets.map((t) => t.id) }),
        });
        console.log(`✅ Sent ${localTickets.length} tickets to server`);

        // Step 2: Retrieve all scanned tickets from server
        let serverResponse = await response.json();

        // Step 3: Update local IndexedDB with server data
        const tx = db.transaction("scanned", "readwrite");
        await tx.objectStore("scanned").clear();
        for (const ticketId of serverResponse.allSeats) {
          await tx.objectStore("scanned").put({ id: ticketId, synced: true });
          scannedTicketsRef.current.add(ticketId);
        }
        await tx.done;
        console.log("✅ Updated local DB with server data");
      } catch (error) {
        console.error("❌ Sync error:", error);
      }
      syncRunning.current = false;
    }, SYNC_INTERVAL);
  };

  function verifyQR(qrContent) {
    if (!qrContent || qrContent.length < 13) return null;
    let decodedKey = atob(SECRET_KEY);
    for (let i = 0; i <= 8; i++) {
      let possibleSeat = qrContent.slice(i, i + 6);
      let remainingChecksum = qrContent.replace(possibleSeat, "");
      let recalculatedChecksum = CryptoJS.HmacSHA256(possibleSeat.toUpperCase(), decodedKey)
        .toString(CryptoJS.enc.Hex)
        .substring(0, 8);
      if (remainingChecksum === recalculatedChecksum) {
        return {
          ticketId: possibleSeat.toUpperCase(),
          seatInfo: {
            zone: possibleSeat.charAt(0),
            floor: possibleSeat.charAt(1),
            row: parseInt(possibleSeat.charAt(2), 16),
            number: possibleSeat.substring(3, 5),
            group: possibleSeat.charAt(5),
          },
        };
      }
    }
    for (let i = 0; i <= 9; i++) {
      let possibleSeat = qrContent.slice(i, i + 5);
      let remainingChecksum = qrContent.replace(possibleSeat, "");
      let recalculatedChecksum = CryptoJS.HmacSHA256(possibleSeat.toUpperCase(), decodedKey)
        .toString(CryptoJS.enc.Hex)
        .substring(0, 8);
      if (remainingChecksum === recalculatedChecksum) {
        return {
          ticketId: possibleSeat.toUpperCase(),
          seatInfo: {
            zone: possibleSeat.charAt(0),
            floor: possibleSeat.charAt(1),
            row: parseInt(possibleSeat.charAt(2), 16),
            number: possibleSeat.substring(3, 4),
            group: possibleSeat.charAt(4),
          },
        };
      }
    }
    return null;
  }

  return (
    <Container maxWidth="sm" sx={{ direction: "rtl", fontFamily: "IRANYekan, Arial, sans-serif" }}>
      <h2 style={{ textAlign: "center" }}>اسکن بلیط کنسرت</h2>
      <div id="reader"></div>
      <Typography variant="caption" color="textSecondary" align="center" display="block">
        برای تایید بلیط به راست بکشید و برای حذف به چپ
        </Typography>
      {/* Render pending tickets with swipe actions */}
      {pendingTickets.length > 0 && (
        <div style={{ marginTop: 10, height:"100px" }}>
          {pendingTickets.map((ticket) => (
            <PendingTicketCard
              key={ticket.ticketId}
              ticket={ticket}
              onAccept={acceptTicket}
              onReject={rejectTicket}
            />
          ))}
        </div>
      )}
      {/* {lastScannedData && <ScannedTicketDetails ticket={lastScannedData} />} */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setToast({ ...toast, open: false })} severity={toast.severity} sx={{ width: "100%" }}>
          {toast.message}
        </Alert>
      </Snackbar>
      {/* For testing toast manually */}
      {/* <Button onClick={() => }> */}
        {/* Test Toast
      </Button> */}
    </Container>
  );
};

export default TicketScanner;
